import request from '@/utils/request'
import { getAdvertDefaultParams } from './constants'
import { AdvertCodes, CategoryNames, HomeAdvertCodes, HomeAdvertParams } from './constants/advertCodes'
import { HOME_CATEGORY_NAV, HOME_BANNER, HOME_NEW_PRODUCT, HOME_NAVIGATION ,HOME_SPECIAL_PRICE} from './constants/advertCodes'

/**
 * 统一的广告列表获取方法
 * @param {string} advertCode - 广告位编码
 * @param {object} params - 自定义参数，会覆盖默认参数
 * @returns {Promise} 请求Promise
 */
export function getAdvertList(advertCode, customParams = {}) {
  const defaultParams = getAdvertDefaultParams(advertCode)
  
  return request({
    url: '/api/Advert/LoadAdvertList',
    method: 'post',
    data: {
      ...defaultParams,
      ...customParams
    }
  })
}

/**
 * 获取分类列表
 * @param {Object} customParams - 自定义参数
 * @returns {Promise}
 */
export function getCategoryList(customParams = {}) {
  return getAdvertList(AdvertCodes.CATEGORY, {
    Kind: 'IMAGE',
    PageIndex: 1,
    PageSize: 20,
    CheckTime: '',
    ...customParams
  })
}

/**
 * 获取Banner列表
 * @param {Object} params - 请求参数
 * @param {string} params.advertCode - 广告位编码(必填)
 * @param {string} params.kind - 类型，默认为'IMAGE'
 * @param {number} params.pageIndex - 页码，默认为1
 * @param {number} params.pageSize - 每页数量，默认为5
 * @returns {Promise} 返回Banner数据
 */
export function getBannerList(params) {
  if (!params.advertCode) {
    throw new Error('advertCode is required');
  }

  return request({
    url: '/api/Advert/LoadAdvertList',
    method: 'post',
    data: {
      AdvertCode: params.advertCode,
      Kind: params.kind || 'IMAGE',
      PageIndex: params.pageIndex || 1,
      PageSize: params.pageSize || 5,
      CheckTime: params.checkTime || ''
    }
  }).then(response => {
    if (response.ErrorCode === 0) {
      return {
        ...response,
        Data: response.Data.map(item => ({
          id: item.Id,
          title: item.Title,
          url: item.Url,
          imageUrl: parseImageUrl(item.ImageUrl)
        }))
      };
    }
    return response;
  });
}

/**
 * 获取新品列表
 * @param {Object} customParams - 自定义参数
 * @returns {Promise} 返回新品列表数据
 */
export function getHomeNewProducts(customParams = {}) {
  return getAdvertList(HOME_NEW_PRODUCT.CODE, {
    ...HOME_NEW_PRODUCT.PARAMS,
    ...customParams
  })
}

/**
 * 获取新品推荐列表，专用于NewProductGrid组件
 * @param {Object} customParams - 自定义参数
 * @returns {Promise} 返回新品列表数据
 */
export function getNewProductsData(customParams = {}) {
  return getAdvertList('NEW_HOME_NEWPRODUCT', {
    Kind: 'PRODUCT',
    PageIndex: 1,
    PageSize: 9,
    ...customParams
  })
}

/**
 * 获取首页·分类导航（横向）列表
 * @param {Object} customParams - 自定义参数
 * @returns {Promise} 返回导航列表数据
 */
export function getHomeNavigationList(customParams = {}) {
  return getAdvertList(HOME_NAVIGATION.CODE, {
    ...HOME_NAVIGATION.PARAMS,
    ...customParams
  })
}

/**
 * 获取特价商品列表
 * @param {Object} params - 请求参数
 * @param {string} params.referenceCode - 参考代码(必填)
 * @param {string} params.referenceKind - 参考种类，默认为'specialactivity'
 * @param {number} params.pageIndex - 页码，默认为1
 * @param {number} params.pageSize - 每页数量，默认为10
 * @param {string} params.priceField - 价格字段名称，默认为'SalePrice'
 * @returns {Promise} 返回特价商品数据
 */
export function getSpecialPriceProducts(params) {
  if (!params.referenceCode) {
    throw new Error('referenceCode is required');
  }

  return request({
    url: '/api/Product/GetReferenceProductList',
    method: 'post',
    data: {
      ReferenceCode: params.referenceCode,
      ReferenceKind: params.referenceKind || 'specialactivity',
      PageIndex: params.pageIndex || 1,
      PageSize: params.pageSize || 10,
      CheckTime: params.checkTime || ''
    }
  }).then(response => {
    if (response.ErrorCode === 0) {
      return {
        ...response,
        Data: response.Data.map(product => ({
          id: product.ProductNo,
          name: product.ProductName,
          price: product[params.priceField || 'Param5'],
          marketprice: product.MarketPrice,    // 映射产品的市场价
          imageUrl: parseImageUrl(product.ProductImage),
          url: product.Url
        }))
      };
    }
    return response;
  });
}

/**
 * 获取分类主推商品列表
 * @param {string} advertCode - 分类广告位编码
 * @param {object} customParams - 自定义参数
 * @returns {Promise}
 */
export function getCategoryFeatureList(advertCode, customParams = {}) {
  return request({
    url: '/api/Advert/LoadAdvertList',
    method: 'post',
    data: {
      AdvertCode: advertCode,
      CheckTine: '',
      Kind: 'PRODUCT',
      PageIndex: 1,
      PageSize: 9,
      ...customParams
    }
  })
}

// 批量请求方法
export async function batchGetCategoryFeature(advertCodes) {
  try {
    const requests = advertCodes.map(code => 
      getCategoryFeatureList(code)
        .then(res => ({
          code,
          data: res.Data || [],
          name: CategoryNames[code] || code
        }))
        .catch(err => {
          console.error(`获取分类 ${code} 数据失败:`, err)
          return {
            code,
            data: [],
            name: CategoryNames[code] || code,
            error: err
          }
        })
    )
    return Promise.all(requests)
  } catch (error) {
    console.error('批量获取分类主推商品失败:', error)
    throw error
  }
}

/**
 * 解析图片URL
 * @param {string} imageUrlString JSON字符串格式的图片URL
 * @returns {string} 返回处理后的完整图片URL
 */
export function parseImageUrl(imageUrlString) {
  try {
    if (!imageUrlString) return '';
    
    // 如果已经是完整的URL，直接返回
    if (imageUrlString.startsWith('http')) {
      return imageUrlString;
    }
    
    const imageData = JSON.parse(imageUrlString);
    if (Array.isArray(imageData) && imageData.length > 0) {
      const { ServerUrl, FilePath } = imageData[0];
      // 确保URL是HTTPS的
      let url = (ServerUrl + FilePath).replace('http:', 'https:');
      // 添加华为云OBS的临时token参数
      if (url.includes('obs.cn-south-1.myhuaweicloud.com')) {
        url += url.includes('?') ? '&' : '?';
        url += 'a='; // 添加临时token参数
      }
      return url;
    }
  } catch (error) {
    console.error('解析图片URL失败:', error, imageUrlString);
  }
  return '';
}

/**
 * 将API响应数据转换为组件所需格式
 * @param {Array} responseData API响应数据
 * @returns {Array} 转换后的分类数据
 */
export function transformCategoryData(responseData) {
  if (!Array.isArray(responseData)) return []
  
  return responseData.map(item => ({
    id: item.Id,
    title: item.Title,
    subtitle: item.Subtitle,
    url: item.Url,
    imageUrl: parseImageUrl(item.ImageUrl)
  }))
}

/**
 * 分类相关接口
 */
export function getCategoryTree(params = { TypeId: 7780 }) {
  return request({
    url: '/api/Article/GetTypeTree',
    method: 'post',
    data: params
  });
}

/**
 * 获取分类商品列表
 * @param {Object} params - 请求参数
 * @param {number} params.TypeId - 分类ID
 * @param {number} params.PageIndex - 页码
 * @param {number} params.PageSize - 每页数量
 * @returns {Promise}
 */
export function getCategoryProducts(params) {
  return request({
    url: '/api/Product/GetProductListOrderbySellCounter',
    method: 'post',
    data: {
      TypeId: params.TypeId,
      PageIndex: params.PageIndex || 1,
      PageSize: params.PageSize || 10
    }
  }).then(response => {
    if (response.ErrorCode === 0 && response.Data?.ProductList) {
      // 处理商品数据
      return {
        ...response,
        Data: {
          List: response.Data.ProductList.map(product => ({
            id: product.ProductNo,
            name: product.ProductName,
            description: product.ShortDesc || '',
            price: product.SalePrice,
            originalPrice: product.MarketPrice,
            imageUrl: parseImageUrl(product.ProductImage),
            saleCount: product.SaleCount,
            url: product.Url,
            status: product.Status,
            favoriteCount: product.FavoriteCount,
            isLike: product.IsLike
          }))
        }
      };
    }
    
    return response;
  }).catch(error => {
    console.error('获取分类商品列表失败:', error);
    throw error;
  });
}

/**
 * 获取首页·分类导航（5x2）列表
 * @param {string} checkTime - 检查时间戳
 * @returns {Promise} 返回分类导航数据
 */
export function getHomeCategoryList(checkTime = '') {
  return getAdvertList(HOME_CATEGORY_NAV.CODE, {
    ...HOME_CATEGORY_NAV.PARAMS,
    CheckTime: checkTime
  })
}

// 保持原有方法名，但内部使用新的实现
// export const getBannerList = getHomeBannerList
export const getNewProducts = getHomeNewProducts
export const getNavigationList = getHomeNavigationList